import { memo, MouseEvent as ReactMouseEvent, useMemo } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import type { GridColDef, GridValidRowModel, GridColumnGroupingModel, DataGridProProps } from '@mui/x-data-grid-pro';
import { renderCellContent } from './renderCellContent';
import { MobileCellClick } from './RowCard';
import { mergeSx } from '../../../utils/styles';

const styles = {
  box: {
    display: 'grid',
    gridTemplateColumns: 'minmax(80px, 30%) 1fr',
    alignItems: 'center',
    gap: '4px 8px',
    '& + &': { mt: 2 },
  },
  caption: {
    fontWeight: 400,
    color: 'text.secondary',
    flexShrink: 0,
  },
  contentText: {
    fontWeight: 500,
    wordBreak: 'break-word',
    m: 0,
    textAlign: 'right',
    width: '100%',
  },
  totalRowText: {
    fontWeight: 700,
  },
  contentWrapper: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    maxWidth: '150px',
    ml: 'auto',
  },
  groupHeader: {
    fontWeight: 700,
    color: 'primary.main',
    mb: 1.5,
  },
  groupBox: {
    p: 2,
    '& + &': {
      borderTop: '1px solid',
      borderColor: 'divider',
    },
  },
  inputField: {
    fontWeight: 500,
    borderRadius: 2,
    maxWidth: '100px',
    marginLeft: 'auto',
  },
  inputProps: {
    textAlign: 'end',
    padding: { xs: '4px' },
  },
};

export type CardContentProps<R extends GridValidRowModel> = {
  row: R;
  columns: readonly GridColDef<R>[];
  rowId: string | number;
  isVisible: (field: string) => boolean;
  onCellClick?: MobileCellClick<R>;
  columnGroupingModel?: GridColumnGroupingModel;
  isTotalsRow?: boolean;
  processRowUpdate?: DataGridProProps<R>['processRowUpdate'];
};

type GroupedColumn<R extends GridValidRowModel> = {
  groupId: string;
  columns: GridColDef<R>[];
};

const CardContentComponent = <R extends GridValidRowModel>({
  row,
  columns,
  rowId,
  isVisible,
  onCellClick,
  columnGroupingModel,
  isTotalsRow,
  processRowUpdate,
}: CardContentProps<R>) => {
  const groupedAndVisibleColumns = useMemo(() => {
    const visibleColumns = columns.filter((col) => isVisible(col.field));

    if (!columnGroupingModel) {
      return [{ groupId: 'ungrouped', columns: visibleColumns }];
    }

    const grouped = visibleColumns.reduce<GroupedColumn<R>[]>((acc, col) => {
      const group = columnGroupingModel.find((group) =>
        group.children.some((child) => 'field' in child && child.field === col.field)
      );

      const groupId = group?.groupId ?? 'ungrouped';

      let groupInAcc = acc.find((group) => group.groupId === groupId);

      if (!groupInAcc) {
        groupInAcc = { groupId, columns: [] };
        acc.push(groupInAcc);
      }

      groupInAcc.columns.push(col);

      return acc;
    }, []);

    return grouped;
  }, [columns, isVisible, columnGroupingModel]);

  return (
    <>
      {groupedAndVisibleColumns.map(({ groupId, columns: groupColumns }) => (
        <Box key={groupId} sx={styles.groupBox}>
          {groupId !== 'ungrouped' && (
            <Typography variant='h6' sx={styles.groupHeader}>
              {groupId}
            </Typography>
          )}
          {groupColumns.map((col) => {
            const content = renderCellContent(col, row, rowId);
            const isTypographyContent = typeof content === 'string' || typeof content === 'number';
            const isEditable = !!col.editable;

            const handleCellClick = (e: ReactMouseEvent<HTMLElement>) => {
              e.stopPropagation();

              if (!onCellClick) return;

              const value = row[col.field as keyof R] as unknown;
              onCellClick({ id: rowId, field: col.field, row, value, colDef: col }, e);
            };

            return (
              <Box key={col.field} sx={styles.box}>
                <Typography variant='caption' sx={styles.caption}>
                  {col.headerName ?? col.field}:
                </Typography>
                {isEditable ? (
                  <TextField
                    sx={styles.inputField}
                    defaultValue={content as string | number | undefined}
                    onClick={handleCellClick}
                    onBlur={async (e) => {
                      const newValue = e.target.value;
                      if (processRowUpdate && newValue !== content?.toString()) {
                        const updatedRow = { ...row, [col.field]: newValue };
                        await processRowUpdate(updatedRow, row, { rowId });
                      }
                    }}
                    slotProps={{
                      htmlInput: { sx: styles.inputProps },
                    }}
                  />
                ) : isTypographyContent ? (
                  <Typography
                    variant='body3'
                    sx={mergeSx(styles.contentText, isTotalsRow ? styles.totalRowText : undefined)}
                    onClick={handleCellClick}
                  >
                    {content}
                  </Typography>
                ) : (
                  <Box sx={styles.contentWrapper} onClick={handleCellClick}>
                    {content}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      ))}
    </>
  );
};

export const CardContent = memo(CardContentComponent) as typeof CardContentComponent;
